import dayjs from 'dayjs'
import timezonePlugin from 'dayjs/plugin/timezone'
import utcPlugin from 'dayjs/plugin/utc'

import { useTimezoneEpochStore } from './timezone-store'

dayjs.extend(utcPlugin)
dayjs.extend(timezonePlugin)

/**
 * The zone every rendered instant is projected into: the viewer's own IANA
 * timezone, else their organization's. `null` means "not resolved yet" — and
 * every helper here then falls back to the browser's zone, which is exactly
 * what the app did before timezones existed. That fallback is the reason
 * shipping this changes nothing until somebody actually picks a zone.
 *
 * Deliberately module state rather than React state: `formatDate` and friends
 * are plain functions called from hundreds of render paths, and threading a
 * zone through all of them would be a rewrite. The zone is resolved once in
 * the dashboard's `beforeLoad` (see {@link resolveEffectiveTimezone}) before
 * any dashboard content paints, so no render ever reads a half-set value.
 */
let effectiveTimezone: string | null = null

/**
 * `America/Chicago` is a zone; `EST` and `+05:00` are not — the backend
 * rejects both with a 422. A bad value must never reach `.tz()`, which throws.
 *
 * The `/` check isn't decoration: modern engines accept a bare offset like
 * `+05:00` as a `timeZone`, so `Intl` alone would wave one through. An IANA
 * identifier is a `Region/City` path, with `UTC` the one legal exception.
 */
export function isValidTimezone(zone: string): boolean {
  if (!zone.includes('/') && zone !== 'UTC') return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone })
    return true
  } catch {
    return false
  }
}

export function setEffectiveTimezone(zone: string | null): void {
  const next = zone !== null && !isValidTimezone(zone) ? null : zone
  const changed = next !== effectiveTimezone
  effectiveTimezone = next
  // Keeps a bare `dayjs.tz(value)` elsewhere in agreement with `inZone`.
  dayjs.tz.setDefault(next ?? undefined)
  // Everything already painted was formatted in the old zone; tell the
  // dashboard to remount its page. See timezone-store for why a re-render
  // would not be enough.
  if (changed) useTimezoneEpochStore.getState().bump()
}

export function getEffectiveTimezone(): string | null {
  return effectiveTimezone
}

/**
 * This device's own zone, as the browser reports it. Always the device — never
 * the effective zone — because the only thing it is used for is offering
 * "use this device's timezone" as a shortcut, and a user who has already
 * picked a zone must still be able to see what their device says.
 *
 * Never write this to the API on the user's behalf. The zone is theirs to
 * choose; an unset zone is reported as unset, not quietly filled in.
 */
export function browserTimezone(): string {
  return dayjs.tz.guess()
}

/** The one shape a calendar day travels in: as a filter param, a bucket key,
 *  a `paid_date`. Never build it by hand — use `toIsoDate` for a picker
 *  `Date` and `dayKey` for an instant. */
export const DAY_KEY_FORMAT = 'YYYY-MM-DD'

/** `2026-09-07` — a calendar day, with no instant behind it. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

/**
 * Parse an API instant and project it into the effective zone. The one entry
 * point every display formatter should go through.
 *
 * Pass nothing for "now in the effective zone". Invalid input is handed back
 * untouched so callers keep their own `isValid()` fallbacks.
 *
 * Bare `YYYY-MM-DD` values are deliberately left alone. `first_available_date`,
 * `paid_date` and the `estimated_*` fields are business calendar days, not
 * moments: dayjs reads them as local midnight, and projecting that into a zone
 * behind the browser would render the day before. Date-only in, date-only out.
 */
export function inZone(value?: dayjs.ConfigType): dayjs.Dayjs {
  const parsed = dayjs(value)
  if (effectiveTimezone === null || !parsed.isValid()) return parsed
  if (typeof value === 'string' && DATE_ONLY.test(value)) return parsed
  return parsed.tz(effectiveTimezone)
}

/**
 * `YYYY-MM-DD` for a calendar day the user picked, read off the local Date the
 * picker produced. Never `toISOString().slice(0, 10)`: that converts to UTC
 * first, so an evening pick west of UTC submits the following day.
 *
 * Picker days are deliberately NOT projected through {@link inZone} — the
 * backend expands a bare day in the viewer's effective zone itself, and
 * converting here would shift it twice.
 */
export function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/**
 * Today as `YYYY-MM-DD`, by default in the effective zone — the day the
 * backend will mean when it expands a bare-day filter for this viewer.
 *
 * Pass a `zone` for the endpoints that read their day in someone else's:
 * `/dashboard/calendar` interprets `start`/`end` in the *organization's*
 * timezone, so the whole team sees the same board. `null` (a zone that hasn't
 * loaded yet) falls back to the browser's day.
 */
export function todayIsoDate(zone?: string | null): string {
  if (zone === undefined) return inZone().format(DAY_KEY_FORMAT)
  if (zone === null || !isValidTimezone(zone)) {
    return dayjs().format(DAY_KEY_FORMAT)
  }
  return dayjs().tz(zone).format(DAY_KEY_FORMAT)
}

/** The picked calendar day, read in the effective zone rather than the
 *  browser's. Accepts the `Date` a picker produces or the `YYYY-MM-DD` a
 *  search param carries; an instant is reduced to the day it falls on. */
function zonedDay(day: Date | string): dayjs.Dayjs {
  const iso =
    typeof day === 'string'
      ? DATE_ONLY.test(day)
        ? day
        : inZone(day).format(DAY_KEY_FORMAT)
      : toIsoDate(day)
  return effectiveTimezone === null
    ? dayjs(iso)
    : dayjs.tz(iso, effectiveTimezone)
}

/**
 * The instant bounds of a picked calendar day, for the few endpoints that take
 * a timestamp rather than a bare `YYYY-MM-DD` (the conversation feed's
 * `from_date` / `to_date`, the chat jump anchor).
 *
 * Bare-day filters must NOT go through these — the backend expands those in
 * the effective zone itself, and pre-expanding here would shift them twice.
 */
export function zonedDayStartIso(day: Date | string): string {
  return zonedDay(day).startOf('day').toISOString()
}

export function zonedDayEndIso(day: Date | string): string {
  return zonedDay(day).endOf('day').toISOString()
}

/** 24-hour `HH:mm`, the shape the time pickers speak. */
const CLOCK_TIME = /^(\d{1,2}):(\d{2})$/

/**
 * The instant a picked calendar day and wall-clock time name together, as the
 * UTC ISO-8601 string an API takes. `null` when the time is not `HH:mm`, or
 * names no real clock reading.
 *
 * Several services refuse a bare date, and rightly: "the 10th at 9am" is a
 * different moment in Tashkent than in New York, and work scheduled on the
 * wrong side of a working day is worse than none. The pair is read together in
 * the viewer's effective zone — 9am means 9am where the agent is sitting — and
 * only then converted.
 *
 * Composed by moving the wall clock inside the zone rather than by adding
 * hours to midnight, so a day containing a daylight-saving change still lands
 * on the time that was picked instead of an hour either side of it.
 */
export function zonedInstantIso(
  day: Date | string,
  time: string,
): string | null {
  const parts = CLOCK_TIME.exec(time)
  if (!parts) return null
  const hour = Number(parts[1])
  const minute = Number(parts[2])
  if (hour > 23 || minute > 59) return null
  return zonedDay(day)
    .hour(hour)
    .minute(minute)
    .second(0)
    .millisecond(0)
    .toISOString()
}
